import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import type { FieldType, SigningField } from '../types/fields';
import { clampFieldToPage, getDefaultFieldSize, getCompactTextFieldSize } from '../utils/coordinates';
import {
  fieldTextLayoutMode,
  formLineBaselineOffsetFromBottom,
  isSingleLineFormText,
} from '../utils/textLayout';
import { applyFieldUpdate, getPageSizeForField } from '../utils/signingFieldState';
import { remapFieldToTargetPage } from '../utils/snapshotRestore';
import { metricsFamilyFromStandardName, resolveStandardFontName, defaultStyleForFieldType } from '../utils/fontMapping';

interface AddFieldInput {
  type: FieldType;
  pageIndex: number;
  pdfX: number;
  pdfY: number;
  renderScale: number;
  /** For form-line fields: 'baseline' = click on underline; 'top' = pdfY is top edge (presets). */
  anchor?: 'top' | 'baseline';
  value?: string;
  imageDataUrl?: string;
  savedSignatureId?: string;
  fontSize?: number;
  isChecked?: boolean;
  // Annotation shape fields (Pass 2) — forwarded directly to the SigningField
  /** Override width in PDF points (used by annotation types whose size is passed by wiring agent). */
  width?: number;
  /** Override height in PDF points. */
  height?: number;
  /** Ink polyline vertices in PDF coords. */
  points?: Array<{ x: number; y: number }>;
  /** Hex outline/stroke color. */
  strokeColor?: string;
  /** Hex fill color. */
  fillColor?: string;
  /** Stroke thickness in PDF points. */
  lineWidth?: number;
  /** Fill opacity (0..1). */
  opacity?: number;
  /** Color used as fill for highlight or default for text. */
  color?: string;
}

/**
 * Cheap, allocation-free change check for a single field. Compares scalars and
 * compares imageDataUrl by reference (so we never walk a multi-MB base64 string).
 * Used to skip no-op history pushes without JSON.stringify-ing the whole array on
 * every drag move.
 */
function fieldChanged(a: SigningField, b: SigningField): boolean {
  return (
    a.x !== b.x ||
    a.y !== b.y ||
    a.width !== b.width ||
    a.height !== b.height ||
    nonGeometryChanged(a, b)
  );
}

/**
 * True when anything OTHER than x/y/width/height differs. Geometry-only updates
 * (drag/resize/nudge) are coalesced into one undo entry per gesture; everything
 * else always gets its own history entry.
 */
function nonGeometryChanged(a: SigningField, b: SigningField): boolean {
  return (
    a.value !== b.value ||
    a.isChecked !== b.isChecked ||
    a.fontSize !== b.fontSize ||
    a.fontFamily !== b.fontFamily ||
    a.bold !== b.bold ||
    a.italic !== b.italic ||
    a.underline !== b.underline ||
    a.strikethrough !== b.strikethrough ||
    a.align !== b.align ||
    a.color !== b.color ||
    a.highlightColor !== b.highlightColor ||
    a.lineHeight !== b.lineHeight ||
    a.letterSpacing !== b.letterSpacing ||
    a.type !== b.type ||
    a.pageIndex !== b.pageIndex ||
    a.group !== b.group ||
    a.savedSignatureId !== b.savedSignatureId ||
    a.imageDataUrl !== b.imageDataUrl ||
    // Annotation shape props (Pass 2)
    a.strokeColor !== b.strokeColor ||
    a.fillColor !== b.fillColor ||
    a.lineWidth !== b.lineWidth ||
    a.opacity !== b.opacity
    // Note: points[] identity changes are treated as geometry (drag translates all points),
    // so we intentionally do NOT compare points here — they share the gesture coalescing path.
  );
}

export function useSigningFields(
  pageSizes: Array<{ width: number; height: number }>,
  onFieldsChanged: () => void
) {
  const [fields, setFields] = useState<SigningField[]>([]);
  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null);

  // Proper multi-level undo/redo history
  const [history, setHistory] = useState<SigningField[][]>([]);
  const [future, setFuture] = useState<SigningField[][]>([]);
  // Synchronous stacks so undo/redo never read a stale closure (keyboard
  // shortcuts call fieldsRef.redo immediately after undo, before re-render).
  const historyRef = useRef<SigningField[][]>([]);
  const futureRef = useRef<SigningField[][]>([]);

  const MAX_HISTORY = 50;

  // Synchronous source of truth for the current fields. Lets mutators read the
  // latest value and push history WITHOUT calling setState inside a setState
  // updater (that pattern double-pushed under React StrictMode, requiring two
  // undos per action). Kept in sync with `fields` for any external setFields.
  const fieldsRef = useRef<SigningField[]>(fields);
  useEffect(() => {
    fieldsRef.current = fields;
  }, [fields]);

  const pushToHistory = useCallback((snapshot: SigningField[]) => {
    setHistory((prevHistory) => {
      const newHistory = [...prevHistory, snapshot];
      const trimmed =
        newHistory.length > MAX_HISTORY ? newHistory.slice(-MAX_HISTORY) : newHistory;
      historyRef.current = trimmed;
      return trimmed;
    });
    futureRef.current = [];
    setFuture([]); // Clear redo stack on new action
  }, []);

  // === GESTURE COALESCING FOR UNDO ===
  // Drag/resize emit a geometry-only updateField per pointermove; without coalescing a
  // single 2-second drag pushed 50+ history entries and evicted the entire undo stack.
  // Rapid geometry-only updates to the SAME field reuse the snapshot pushed at the start
  // of the gesture, so Ctrl+Z steps back one whole gesture. The gesture ends on
  // pointerup/pointercancel (drag/resize) or after a short pause (keyboard nudges, which
  // have no pointerup — discrete nudges spaced apart still get their own entries while
  // key-repeat streams coalesce).
  const GESTURE_COALESCE_MS = 400;
  const geometryGestureRef = useRef<{ fieldId: string; lastTime: number } | null>(null);
  // Tracks the id of a field that was just added and has not yet received its first
  // non-geometry (content) commit. The first such commit is coalesced into the addField
  // history entry so that Ctrl+Z after place-and-type removes the field in one step.
  const justAddedFieldIdRef = useRef<string | null>(null);
  /** Internal clipboard for Ctrl+C / Ctrl+V field copy-paste across pages. */
  const fieldClipboardRef = useRef<{
    sourcePageIndex: number;
    // Fix 2: capture source page visual dims at copy time so pasteField scaling
    // remains correct even after a page delete/reorder changes the live pageSizes array.
    sourcePageWidth: number;
    sourcePageHeight: number;
    template: Omit<SigningField, 'id' | 'pageIndex'>;
  } | null>(null);
  const pasteCountRef = useRef(0);

  useEffect(() => {
    const endGesture = () => {
      geometryGestureRef.current = null;
    };
    window.addEventListener('pointerup', endGesture);
    window.addEventListener('pointercancel', endGesture);
    return () => {
      window.removeEventListener('pointerup', endGesture);
      window.removeEventListener('pointercancel', endGesture);
    };
  }, []);

  const commit = useCallback((next: SigningField[]) => {
    fieldsRef.current = next;
    setFields(next);
  }, []);

  const addField = useCallback((input: AddFieldInput) => {
    // Caller may supply explicit width/height (e.g. annotation placement from wiring agent).
    let { width, height } = input.width != null && input.height != null
      ? { width: input.width, height: input.height }
      : getDefaultFieldSize(input.type, input.renderScale);

    // Annotation shape types (highlight/rect/ellipse/ink) use geometry passed by the
    // wiring agent directly — skip text compaction and baseline anchor logic.
    const isAnnotationType =
      input.type === 'highlight' ||
      input.type === 'underline' ||
      input.type === 'strikeout' ||
      input.type === 'rect' ||
      input.type === 'ellipse' ||
      input.type === 'cross' ||
      input.type === 'text-bubble' ||
      input.type === 'ink';

    // Tight box for short text fields (signature, date, name, initials, single-line text)
    const compactTypes = ['signature-type', 'date', 'name', 'initials'] as const;
    const fs = input.fontSize ?? (input.type === 'signature-type' ? 16 : 13);
    const shouldCompact =
      !isAnnotationType && (
        compactTypes.includes(input.type as (typeof compactTypes)[number]) ||
        (input.type === 'text' && isSingleLineFormText(input.value))
      );
    if (shouldCompact) {
      const placeScale = input.renderScale > 0 ? input.renderScale : 1;
      const tight = getCompactTextFieldSize(
        input.type,
        input.value ?? '',
        fs,
        placeScale
      );
      width = tight.width;
      height = tight.height;
    }

    const pageSize = getPageSizeForField({ pageIndex: input.pageIndex }, pageSizes);
    if (!pageSize) {
      // Page metadata missing (0-page doc, stale pageIndex from a template
      // import, etc.). Refuse to place a field — it would be clamped against
      // a misleading Letter-sized box and either overflow or land in the
      // wrong quadrant of a rotated page.
      console.warn(`useSigningFields.addField: refusing to place field on missing page ${input.pageIndex}`);
      return null;
    }
    const fontSize = fs;
    const layoutMode = fieldTextLayoutMode(input.type, {
      boxHeight: height,
      value: input.value,
    });
    const useBaselineAnchor = input.anchor === 'baseline';
    // H-3: resolve the field's actual font family for an accurate baseline offset
    // (helvetica was previously hardcoded, breaking serif/mono/script placements).
    const resolvedDefaults = defaultStyleForFieldType(input.type);
    const resolvedFontFamily = resolvedDefaults.fontFamily;
    const fieldFontFamily = metricsFamilyFromStandardName(
      resolveStandardFontName(resolvedFontFamily, resolvedDefaults.bold, resolvedDefaults.italic)
    );
    // Annotation types: pdfY is the top-left click position; just use y as-is (top origin).
    // Presets omit anchor (top-left). Viewer clicks pass anchor: 'baseline' for form-line fields.
    const boxY = isAnnotationType
      ? Math.max(0, input.pdfY - height)
      : useBaselineAnchor && layoutMode === 'form-line'
        ? Math.max(
            0,
            input.pdfY -
              formLineBaselineOffsetFromBottom(
                fontSize,
                fieldFontFamily
              )
          )
        : Math.max(0, input.pdfY - height);

    const field = clampFieldToPage({
      id: uuidv4(),
      type: input.type,
      pageIndex: input.pageIndex,
      x: input.pdfX,
      y: boxY,
      width,
      height,
      value: input.value,
      imageDataUrl: input.imageDataUrl,
      savedSignatureId: input.savedSignatureId,
      fontSize: input.fontSize,
      isChecked: input.isChecked,
      // Annotation shape props — only populated when provided
      ...(input.color !== undefined && { color: input.color }),
      ...(input.points !== undefined && { points: input.points }),
      ...(input.strokeColor !== undefined && { strokeColor: input.strokeColor }),
      ...(input.fillColor !== undefined && { fillColor: input.fillColor }),
      ...(input.lineWidth !== undefined && { lineWidth: input.lineWidth }),
      ...(input.opacity !== undefined && { opacity: input.opacity }),
    }, pageSize.width, pageSize.height);

    const prev = fieldsRef.current;
    pushToHistory(prev);
    geometryGestureRef.current = null;
    commit([...prev, field]);
    setSelectedFieldId(field.id);
    justAddedFieldIdRef.current = field.id;
    onFieldsChanged();
    return field;
  }, [onFieldsChanged, pageSizes, pushToHistory, commit]);

  const updateField = useCallback((id: string, updates: Partial<SigningField>) => {
    const prev = fieldsRef.current;
    const index = prev.findIndex((field) => field.id === id);
    if (index === -1) return;

    const updated = applyFieldUpdate(prev[index], updates, pageSizes);
    // Only push to history if something actually changed (cheap per-field compare)
    if (!fieldChanged(prev[index], updated)) return;

    if (nonGeometryChanged(prev[index], updated)) {
      // Value/style/structure change — always its own undo step, EXCEPT for the very
      // first content commit of a newly-added field (place-and-type coalescing): fold
      // that first commit into the addField history entry so one Ctrl+Z removes both.
      if (justAddedFieldIdRef.current === id) {
        justAddedFieldIdRef.current = null; // consume: subsequent edits get their own steps
        // skip pushToHistory — coalesced into the addField history step
      } else {
        pushToHistory(prev);
      }
      geometryGestureRef.current = null;
    } else {
      // Geometry-only (drag/resize/nudge): one undo step per gesture, not per move.
      // A geometry update on the newly-added field (e.g. the user drags before typing)
      // breaks the coalescing window so subsequent content edits get their own steps.
      if (justAddedFieldIdRef.current === id) {
        justAddedFieldIdRef.current = null;
      }
      const now = Date.now();
      const gesture = geometryGestureRef.current;
      const continuesGesture =
        gesture !== null && gesture.fieldId === id && now - gesture.lastTime < GESTURE_COALESCE_MS;
      if (!continuesGesture) {
        pushToHistory(prev);
      }
      geometryGestureRef.current = { fieldId: id, lastTime: now };
    }

    const next = prev.slice();
    next[index] = updated;
    commit(next);
    onFieldsChanged();
  }, [onFieldsChanged, pageSizes, pushToHistory, commit]);

  /**
   * Batched multi-field update: apply a partial patch to many fields at once as a
   * SINGLE undo step with ONE onFieldsChanged commit. Used by bulk text-formatting
   * (e.g. Bold across N selected fields) so reverting the whole action takes one
   * Ctrl+Z instead of N. Each field reuses the same merge+clamp logic as
   * updateField (applyFieldUpdate against pageSizes). Ids that don't exist, or
   * whose merge produces no real change, are skipped. If nothing changes overall,
   * no history entry is pushed and onFieldsChanged is not fired.
   */
  const updateFields = useCallback((updatesById: Record<string, Partial<SigningField>>) => {
    const prev = fieldsRef.current;
    const ids = Object.keys(updatesById);
    if (ids.length === 0) return;

    let next: SigningField[] | null = null;
    for (let index = 0; index < prev.length; index++) {
      const current = (next ?? prev)[index];
      const updates = updatesById[current.id];
      if (updates === undefined) continue;
      const updated = applyFieldUpdate(current, updates, pageSizes);
      if (!fieldChanged(current, updated)) continue;
      if (next === null) next = prev.slice();
      next[index] = updated;
    }

    // Nothing actually changed across all targeted fields — no history, no commit.
    if (next === null) return;

    // A bulk content/style change is its own distinct undo step. Break the
    // place-and-type and geometry-gesture coalescing windows so it never folds
    // into an unrelated prior entry.
    justAddedFieldIdRef.current = null;
    pushToHistory(prev);
    geometryGestureRef.current = null;
    commit(next);
    onFieldsChanged();
  }, [onFieldsChanged, pageSizes, pushToHistory, commit]);

  const deleteField = useCallback((id: string) => {
    const prev = fieldsRef.current;
    const next = prev.filter((field) => field.id !== id);
    if (next.length === prev.length) return;

    // Break coalescing window: a delete is its own distinct action.
    if (justAddedFieldIdRef.current === id) {
      justAddedFieldIdRef.current = null;
    }
    pushToHistory(prev);
    geometryGestureRef.current = null;
    commit(next);
    setSelectedFieldId((current) => (current === id ? null : current));
    onFieldsChanged();
  }, [onFieldsChanged, pushToHistory, commit]);

  /** Clone a field onto the same page with a small offset; selects the clone. */
  const duplicateField = useCallback((id: string) => {
    const prev = fieldsRef.current;
    const source = prev.find((field) => field.id === id);
    if (!source) return null;

    const pageSize = getPageSizeForField(source, pageSizes);
    if (!pageSize) {
      // Page metadata missing for the source field; bail rather than clamp
      // against Letter and corrupt the duplicate.
      console.warn(`useSigningFields.duplicateField: source field ${id} has missing/invalid page`);
      return null;
    }
    const clone = clampFieldToPage(
      { ...source, id: uuidv4(), x: source.x + 12, y: source.y - 12 },
      pageSize.width,
      pageSize.height
    );
    pushToHistory(prev);
    geometryGestureRef.current = null;
    commit([...prev, clone]);
    setSelectedFieldId(clone.id);
    onFieldsChanged();
    return clone;
  }, [onFieldsChanged, pageSizes, pushToHistory, commit]);

  /**
   * Place a copy of the field at the same position on every OTHER page (single undo
   * entry). Primary use: initialing/dating every page of a contract.
   * Returns how many copies were placed.
   */
  const duplicateFieldToAllPages = useCallback((id: string) => {
    const prev = fieldsRef.current;
    const source = prev.find((field) => field.id === id);
    if (!source || pageSizes.length < 2) return 0;

    // Field coords are stored in VISUAL space, so all scaling/clamping must use
    // VISUAL page dimensions. For a 90/270-rotated page the visual width is the
    // media HEIGHT, so reading the raw pageSizes entry would transpose the box.
    // getPageSizeForField/getVisualPageSize perform that conversion (and guard
    // missing/invalid entries) — mirror the other geometry sites in this hook.
    const sourceVisual = getPageSizeForField(source, pageSizes);
    if (!sourceVisual) {
      // Stale source pageIndex (e.g. template import against a shorter doc):
      // bail rather than scale against a misleading box and corrupt every copy.
      console.warn(`useSigningFields.duplicateFieldToAllPages: source field ${id} has missing/invalid page`);
      return 0;
    }

    const clones: SigningField[] = [];
    for (let pageIndex = 0; pageIndex < pageSizes.length; pageIndex++) {
      if (pageIndex === source.pageIndex) continue;
      const targetVisual = getPageSizeForField({ pageIndex }, pageSizes);
      // Skip pages with missing/invalid metadata rather than emit a NaN-clamped
      // copy that would poison history and the server payload.
      if (!targetVisual) continue;
      // Place the copy at the same PROPORTIONAL position so it lands in the same
      // visual spot on pages of different sizes (mixed Letter/A4/landscape docs).
      // Keep the box dimensions fixed — initials/dates shouldn't grow with the page.
      clones.push(
        remapFieldToTargetPage(
          { ...source, id: uuidv4(), pageIndex },
          sourceVisual,
          targetVisual
        )
      );
    }
    if (clones.length === 0) return 0;

    pushToHistory(prev);
    geometryGestureRef.current = null;
    commit([...prev, ...clones]);
    onFieldsChanged();
    return clones.length;
  }, [onFieldsChanged, pageSizes, pushToHistory, commit]);

  /** Store the selected field in the internal clipboard for cross-page paste. */
  const copyField = useCallback((id: string): boolean => {
    const source = fieldsRef.current.find((field) => field.id === id);
    if (!source) return false;
    const { id: _id, pageIndex, ...template } = source;
    // Fix 2: snapshot the source page's visual dimensions at copy time so that
    // pasteField scaling is insulated from subsequent page deletes/reorders that
    // would change pageSizes[sourcePageIndex] to a different page's size.
    const sourceVisual = getPageSizeForField({ pageIndex }, pageSizes);
    const sourcePageWidth = sourceVisual?.width ?? 0;
    const sourcePageHeight = sourceVisual?.height ?? 0;
    fieldClipboardRef.current = { sourcePageIndex: pageIndex, sourcePageWidth, sourcePageHeight, template };
    pasteCountRef.current = 0;
    return true;
  }, [pageSizes]);

  const canPasteField = useCallback((): boolean => fieldClipboardRef.current !== null, []);

  /**
   * Paste the clipboard field onto `targetPageIndex`. Each paste nudges the copy
   * slightly so repeated pastes on the same page do not stack exactly.
   */
  const pasteField = useCallback((targetPageIndex: number): SigningField | null => {
    const clip = fieldClipboardRef.current;
    if (!clip) return null;

    const targetVisual = getPageSizeForField({ pageIndex: targetPageIndex }, pageSizes);
    if (!targetVisual) {
      console.warn(
        `useSigningFields.pasteField: target page ${targetPageIndex} has missing/invalid metadata`
      );
      return null;
    }

    pasteCountRef.current += 1;
    const n = pasteCountRef.current;

    // Fix 2: use the captured source dims from copyField instead of a live
    // pageSizes lookup, so a page delete/reorder between copy and paste does
    // not silently swap in a different page's dimensions and mis-scale the field.
    let fieldToPlace: SigningField = {
      ...clip.template,
      id: uuidv4(),
      pageIndex: targetPageIndex,
    };

    if (clip.sourcePageIndex !== targetPageIndex && clip.sourcePageWidth > 0 && clip.sourcePageHeight > 0) {
      fieldToPlace = remapFieldToTargetPage(
        fieldToPlace,
        { width: clip.sourcePageWidth, height: clip.sourcePageHeight },
        targetVisual
      );
    } else {
      fieldToPlace = clampFieldToPage(
        fieldToPlace,
        targetVisual.width,
        targetVisual.height
      );
    }

    fieldToPlace = clampFieldToPage(
      { ...fieldToPlace, x: fieldToPlace.x + n * 12, y: fieldToPlace.y - n * 12 },
      targetVisual.width,
      targetVisual.height
    );

    const clone = fieldToPlace;

    const prev = fieldsRef.current;
    pushToHistory(prev);
    geometryGestureRef.current = null;
    commit([...prev, clone]);
    setSelectedFieldId(clone.id);
    onFieldsChanged();
    return clone;
  }, [onFieldsChanged, pageSizes, pushToHistory, commit]);

  const undo = useCallback(() => {
    const hist = historyRef.current;
    if (hist.length === 0) return;

    geometryGestureRef.current = null;
    const previous = hist[hist.length - 1];
    const current = fieldsRef.current;
    const nextHistory = hist.slice(0, -1);
    const nextFuture = [current, ...futureRef.current];
    historyRef.current = nextHistory;
    futureRef.current = nextFuture;
    setHistory(nextHistory);
    setFuture(nextFuture);

    // Fix 1: clear selection if the restored snapshot no longer contains the
    // selected id. The old Array.find() approach only detected the FIRST removed
    // field, so multi-add actions (e.g. duplicateFieldToAllPages) left a ghost
    // selection pointing at a deleted id for every field after the first.
    // Mirror the redo path's pattern which correctly handles the general case.
    setSelectedFieldId((curr) => (curr && previous.some((f) => f.id === curr) ? curr : null));

    commit(previous);
    onFieldsChanged();
  }, [onFieldsChanged, commit]);

  const redo = useCallback(() => {
    const fut = futureRef.current;
    if (fut.length === 0) return;

    geometryGestureRef.current = null;
    const nextState = fut[0];
    const current = fieldsRef.current;
    const nextFuture = fut.slice(1);
    const nextHistory = [...historyRef.current, current];
    futureRef.current = nextFuture;
    historyRef.current = nextHistory;
    setFuture(nextFuture);
    setHistory(nextHistory);

    // Drop selection if it points at a field that doesn't exist in the redone state.
    setSelectedFieldId((curr) => (curr && nextState.some((f) => f.id === curr) ? curr : null));

    commit(nextState);
    onFieldsChanged();
  }, [onFieldsChanged, commit]);

  /** Replace all fields after page mutations (with history). */
  const replaceFields = useCallback(
    (next: SigningField[]) => {
      const prev = fieldsRef.current;
      if (prev.length > 0 || next.length > 0) {
        pushToHistory(prev);
      }
      geometryGestureRef.current = null;
      commit(next);
      setSelectedFieldId((curr) => (curr && next.some((f) => f.id === curr) ? curr : null));
      onFieldsChanged();
    },
    [onFieldsChanged, pushToHistory, commit]
  );

  /**
   * Replace all fields WITHOUT pushing a history entry. Used by reorder's
   * optimistic client-side remap so a single reorder produces exactly ONE undo
   * entry (the authoritative server-result remap inside applyMutation pushes it).
   */
  const replaceFieldsNoHistory = useCallback(
    (next: SigningField[]) => {
      geometryGestureRef.current = null;
      commit(next);
      setSelectedFieldId((curr) => (curr && next.some((f) => f.id === curr) ? curr : null));
      onFieldsChanged();
    },
    [onFieldsChanged, commit]
  );

  const clearFields = useCallback(() => {
    const prev = fieldsRef.current;
    if (prev.length > 0) {
      // Contract #2: clearFields pushes a SINGLE history entry.
      pushToHistory(prev);
    }
    geometryGestureRef.current = null;
    commit([]);
    setSelectedFieldId(null);
    futureRef.current = [];
    setFuture([]);
    onFieldsChanged();
  }, [onFieldsChanged, pushToHistory, commit]);

  /**
   * Contract #3: History-aware restore — replaces all fields with `next`,
   * pushes a single undo entry, fires onFieldsChanged, and marks signed output
   * stale (via onFieldsChanged). Use this for snapshot/template restore instead
   * of the raw setFields.
   */
  const loadFields = useCallback((next: SigningField[]) => {
    const prev = fieldsRef.current;
    pushToHistory(prev);
    geometryGestureRef.current = null;
    commit(next);
    setSelectedFieldId((curr) => (curr && next.some((f) => f.id === curr) ? curr : null));
    futureRef.current = [];
    setFuture([]);
    onFieldsChanged();
  }, [onFieldsChanged, pushToHistory, commit]);

  // Lightweight native drag-to-reorder support for Placed Fields list (high-impact organization)
  const reorderFields = useCallback((fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0) return;
    const prev = fieldsRef.current;
    if (fromIndex >= prev.length || toIndex >= prev.length) return;

    pushToHistory(prev);
    const next = prev.slice();
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    commit(next);
    onFieldsChanged();
  }, [onFieldsChanged, pushToHistory, commit]);

  const canUndo = history.length > 0;
  const canRedo = future.length > 0;

  // Memoize selectedField on a stable key (selectedFieldId) so consumers that
  // only care about WHICH field is selected don't re-render when fields[] is
  // mutated (every drag) unless the selection actually changes.
  const selectedField = useMemo(
    () => fields.find((field) => field.id === selectedFieldId) || null,
    [fields, selectedFieldId]
  );

  // Actions object is intentionally built ONCE and never memoized on `fields`.
  // Every entry is a stable useCallback, so consumers like PdfViewer / Toolbar
  // that destructure `{ addField, updateField, ... }` get reference-stable
  // handlers across all drag/resize/keyboard events. Previously the surrounding
  // useMemo rebuilt on every `fields` change, which propagated to every
  // downstream React.memo bailed child.
  const actions = useMemo(
    () => ({
      addField,
      updateField,
      updateFields,
      deleteField,
      duplicateField,
      duplicateFieldToAllPages,
      copyField,
      canPasteField,
      pasteField,
      undo,
      redo,
      clearFields,
      loadFields,
      replaceFields,
      replaceFieldsNoHistory,
      reorderFields,
    }),
    [
      addField,
      updateField,
      updateFields,
      deleteField,
      duplicateField,
      duplicateFieldToAllPages,
      copyField,
      canPasteField,
      pasteField,
      undo,
      redo,
      clearFields,
      loadFields,
      replaceFields,
      replaceFieldsNoHistory,
      reorderFields,
    ]
  );

  // `state` (fields + selection) changes on every drag; `actions` does not.
  // The returned object spreads them so the consumer contract is unchanged.
  return useMemo(
    () => ({
      fields,
      selectedField,
      selectedFieldId,
      canUndo,
      canRedo,
      setFields,
      setSelectedFieldId,
      ...actions,
    }),
    [fields, selectedField, selectedFieldId, canUndo, canRedo, actions]
  );
}
